import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "@/App";
import { Radio } from "lucide-react";

/**
 * Small pulsing badge that shows how many prop-firm Bridge EAs are currently online.
 * Green = at least one bridge pinged in the last 5 minutes.
 * Grey  = no accounts configured yet.
 * Red   = accounts exist but none has pinged in the last 5 min.
 * Polls /api/propfirm/accounts every 30s.
 */
export default function BridgeHealthBadge({ compact = false }) {
  const [state, setState] = useState({ total: 0, online: 0, lastPingSec: null });

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const r = await axios.get(`${API}/propfirm/accounts`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!alive) return;
        const items = r.data.items || [];
        let online = 0;
        let minSec = null;
        const now = Date.now();
        for (const a of items) {
          if (a.last_seen_at) {
            const diff = Math.floor((now - new Date(a.last_seen_at).getTime()) / 1000);
            if (diff < 300) online += 1;
            if (minSec === null || diff < minSec) minSec = diff;
          }
        }
        setState({ total: items.length, online, lastPingSec: minSec });
      } catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const { total, online, lastPingSec } = state;
  let color = "bg-white/20";
  let ring  = "";
  let label = "Aucun compte";
  let sub   = "Configurer un compte";

  if (total > 0) {
    if (online > 0) {
      color = "bg-accent-green";
      ring  = "ring-4 ring-accent-green/25 animate-pulse";
      label = `${online}/${total} Bridge${online > 1 ? "s" : ""} en ligne`;
      sub   = lastPingSec !== null ? `Dernier ping ${lastPingSec}s` : "En attente…";
    } else {
      color = "bg-red-500";
      label = `${total} compte${total > 1 ? "s" : ""} hors ligne`;
      sub   = lastPingSec !== null ? `Silencieux depuis ${Math.floor(lastPingSec / 60)} min` : "Aucun ping";
    }
  }

  const content = (
    <>
      <span className={`w-2 h-2 rounded-full ${color} ${ring}`} />
      {!compact && (
        <span className="flex flex-col leading-tight">
          <span className="text-[11px] font-semibold text-white/80">{label}</span>
          <span className="text-[9px] text-white/40">{sub}</span>
        </span>
      )}
    </>
  );

  return (
    <Link
      to="/app/my-accounts"
      className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 transition"
      data-testid="bridge-health-badge"
      title={`${label} — ${sub}`}
    >
      <Radio size={12} className="text-white/50" />
      {content}
    </Link>
  );
}
